import React, { useEffect, useMemo } from 'react';
import {
  useAuth,
  setLastActivityTime,
  getLastActivityTime,
} from '../../../../context/AuthContext';
import { useHistory } from 'react-router-dom';
import { routes as authRoutes } from '../../../auth/routes';
import { useUserInfo } from '../../context/UserInfo';
import { deleteTokens } from '../../../../api/jwtAuth/jwtAuthApi';

const INACTIVITY_DEFAULT_TIMEOUT_MINUTES = 15;

export const AutoLogout: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { logout } = useAuth();
  const history = useHistory();
  const { inactivityTimeout: inactivityTimeoutSf } = useUserInfo();

  const inactivityTimeoutMinutes: number | null = useMemo(() => {
    // Disable auto-logout in development mode
    if (import.meta.env.MODE !== 'production') {
      return null;
    }
    // Zero and negative values disable auto-logout
    if (inactivityTimeoutSf !== null && inactivityTimeoutSf <= 0) {
      return null;
    }
    // If SF value is not set then use default value
    return inactivityTimeoutSf ?? INACTIVITY_DEFAULT_TIMEOUT_MINUTES;
  }, [inactivityTimeoutSf]);

  useEffect(() => {
    const logoutAndRedirect = async () => {
      try {
        await deleteTokens();
      } catch (error) {
        console.error(
          'Failed to delete tokens during inactivity logout',
          error,
        );
      }
      logout();
      history.push(authRoutes.sessionTimeout.route.create({}));
    };

    if (inactivityTimeoutMinutes === null) {
      setLastActivityTime();
      return;
    }

    const timeoutMs = inactivityTimeoutMinutes * 60 * 1000;
    const lastActivity = getLastActivityTime();
    if (lastActivity !== null && Date.now() - lastActivity > timeoutMs) {
      logoutAndRedirect();
      return;
    }

    let timeout: NodeJS.Timeout | null = null;

    const resetTimeout = () => {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      setLastActivityTime();
      timeout = setTimeout(() => {
        logoutAndRedirect();
      }, timeoutMs);
    };

    window.addEventListener('mousemove', resetTimeout);
    window.addEventListener('keydown', resetTimeout);

    resetTimeout();

    return () => {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      window.removeEventListener('mousemove', resetTimeout);
      window.removeEventListener('keydown', resetTimeout);
    };
  }, [logout, history, inactivityTimeoutMinutes]);

  return null;
};
